import React, { useRef, useState } from "react";
import { Upload, Link2, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Reusable uploader: shows preview, supports file upload + URL paste. Calls onChange(url).
export default function ImageUploader({ value, onChange, label = "Imagen", testid = "uploader", compact = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState("upload");
  const [urlInput, setUrlInput] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Imagen subida");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo subir la imagen");
    } finally { setUploading(false); }
  };

  return (
    <div data-testid={testid}>
      {label && <label className="text-sm font-medium text-nx-dark">{label}</label>}
      {value ? (
        <div className="relative mt-1.5 group">
          <img src={value} alt="preview" className={`w-full rounded-md object-cover ${compact ? "h-32" : "aspect-video"}`} />
          <button type="button" onClick={() => onChange("")} data-testid={`${testid}-remove`}
            className="absolute top-2 right-2 bg-black/70 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors">
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="mt-1.5">
          <div className="flex gap-2 mb-2">
            <button type="button" onClick={() => setMode("upload")} className={`text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 ${mode === "upload" ? "bg-nx-dark text-white" : "bg-gray-100 text-gray-500"}`} data-testid={`${testid}-mode-upload`}><Upload size={13} /> Subir</button>
            <button type="button" onClick={() => setMode("url")} className={`text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 ${mode === "url" ? "bg-nx-dark text-white" : "bg-gray-100 text-gray-500"}`} data-testid={`${testid}-mode-url`}><Link2 size={13} /> URL</button>
          </div>
          {mode === "upload" ? (
            <div onClick={() => !uploading && inputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center cursor-pointer hover:border-nx-orange transition-colors">
              {uploading ? <Loader2 className="mx-auto animate-spin text-nx-orange" /> : <>
                <Upload className="mx-auto text-gray-400 mb-1" size={22} />
                <p className="text-xs text-gray-500">Hacé clic para subir desde tu computadora</p>
                <p className="text-[10px] text-gray-400 mt-1">JPG, PNG, WEBP o GIF · máx 8MB</p>
              </>}
              <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={`${testid}-file`}
                onChange={(e) => handleFile(e.target.files?.[0])} />
            </div>
          ) : (
            <div className="flex gap-2">
              <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://…"
                data-testid={`${testid}-url-input`} className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm" />
              <button type="button" onClick={() => { if (urlInput.trim()) { onChange(urlInput.trim()); setUrlInput(""); } }}
                className="bg-nx-dark text-white px-3 py-2 rounded-md text-xs font-bold">Usar</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
